/**
 * The connectivity panel (PLAN §10.2), always in the top bar: a compact
 * status — a tone dot, the operator message, the primary's latency — that
 * expands into every watched endpoint (reachable, latency, version, the
 * node's cloud link and outbox), the primary's socket, the cloud's view of
 * the site, and "Switch to local" when it is on offer.
 *
 * Presentational: the view model comes in (`connectivity/view.ts`, built from
 * the manager's state by `views/ConnectivityControl.tsx`), and the one action
 * goes out as `onChoosePrimary(endpointId | null)` (null: back to the default
 * order).
 */
import { ActivityIcon, CloudIcon, ServerIcon } from 'lucide-react';
import type { ConnectivityTone } from '../connectivity/describe.js';
import type { Reach, SocketState } from '../connectivity/state.js';
import type { ConnectivityView, EndpointRow } from '../connectivity/view.js';
import { cn } from '../lib/utils.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover.js';

export type ConnectivityStatusProps = {
  readonly view: ConnectivityView;
  readonly onChoosePrimary: (endpointId: string | null) => void;
  /** Open on first render (stories and specs); the popover is otherwise the operator's. */
  readonly defaultOpen?: boolean;
};

const DOT: Record<ConnectivityTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  standby: 'bg-standby',
  destructive: 'bg-destructive',
};

const REACH: Record<Reach, { variant: 'success' | 'warning' | 'standby'; text: string }> = {
  up: { variant: 'success', text: 'reachable' },
  down: { variant: 'warning', text: 'unreachable' },
  unknown: { variant: 'standby', text: 'checking' },
};

const SOCKET: Record<SocketState, string> = {
  unknown: 'no live connection',
  connecting: 'live connection: connecting',
  open: 'live connection: open',
  closed: 'live connection: closed, reconnecting',
};

function ms(value: number | null): string {
  return value === null ? '–' : `${String(value)} ms`;
}

function EndpointLine({ row }: { readonly row: EndpointRow }) {
  const reach = REACH[row.reach];
  const Icon = row.kind === 'cloud' ? CloudIcon : ServerIcon;
  return (
    <li className="flex flex-col gap-1 border-t py-2 first:border-t-0" data-testid="connectivity-endpoint" data-endpoint={row.id} data-reach={row.reach}>
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-sm font-medium">{row.label}</span>
        {row.primary ? (
          <Badge variant="outline" data-testid="primary-marker">
            primary
          </Badge>
        ) : null}
        <Badge variant={reach.variant} className="ml-auto">
          {reach.text}
        </Badge>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 text-xs text-muted-foreground">
        <dt>Latency</dt>
        <dd className="text-foreground">{ms(row.latencyMs)}</dd>
        <dt>Version</dt>
        <dd className="truncate text-foreground">{row.version === null ? '–' : `${row.version}${row.stage === null ? '' : ` · ${row.stage}`}`}</dd>
        {row.kind === 'node' ? (
          <>
            <dt>Cloud link</dt>
            <dd className="text-foreground" data-testid="cloud-link">
              {row.cloudLink ?? '–'}
            </dd>
            <dt>Outbox</dt>
            <dd className="text-foreground" data-testid="outbox">
              {row.outboxDepth === null ? '–' : String(row.outboxDepth)}
            </dd>
            {row.witness === null ? null : (
              <>
                <dt>Witness</dt>
                <dd className="text-foreground" data-testid="witness">
                  {row.witness}
                </dd>
              </>
            )}
          </>
        ) : null}
      </dl>
      {row.reach === 'up' || row.error === null ? null : (
        <p className="text-xs text-muted-foreground">
          {row.error}
          {row.lastSeen === null ? '' : ` · last answered ${row.lastSeen}`}
        </p>
      )}
    </li>
  );
}

export function ConnectivityStatus({ view, onChoosePrimary, defaultOpen }: ConnectivityStatusProps) {
  const { summary } = view;
  return (
    <Popover {...(defaultOpen === true ? { defaultOpen: true } : {})}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="connectivity-status" data-case={summary.case} aria-label={`Connectivity: ${summary.headline}`}>
          <span className={cn('size-2.5 shrink-0 rounded-full', DOT[summary.tone])} aria-hidden="true" />
          <span className="max-w-56 truncate">{summary.headline}</span>
          {view.primary.latencyMs === null ? null : <span className="hidden text-xs text-muted-foreground md:inline">{ms(view.primary.latencyMs)}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96" data-testid="connectivity-panel">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Badge variant={summary.tone} data-testid="connectivity-headline">
              {summary.headline}
            </Badge>
            <p className="text-sm" data-testid="connectivity-detail">
              {summary.detail}
            </p>
            {summary.readOnly ? (
              <Badge variant="warning" data-testid="connectivity-read-only">
                Read-only from here: edits cannot reach the studio
              </Badge>
            ) : null}
          </div>
          <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="connectivity-primary">
            <ActivityIcon className="size-3.5" aria-hidden="true" />
            Primary: {view.primary.kind === 'cloud' ? 'Cloud' : 'Node'} · {view.primary.label} · {SOCKET[view.socket]}
          </p>
          {view.offerLocal === null ? null : (
            <Button size="sm" data-testid="switch-to-local" onClick={() => onChoosePrimary(view.offerLocal?.id ?? null)}>
              Switch to local — {view.offerLocal.label}
            </Button>
          )}
          {view.chosen ? (
            <Button size="sm" variant="outline" data-testid="use-default-primary" onClick={() => onChoosePrimary(null)}>
              Use the default connection
            </Button>
          ) : null}
          <ul aria-label="Endpoints">
            {view.endpoints.map((row) => (
              <EndpointLine key={row.id} row={row} />
            ))}
          </ul>
          {view.cloudView === null || view.cloudView.length === 0 ? null : (
            <div className="flex flex-col gap-1 border-t pt-2" data-testid="cloud-view">
              <p className="text-xs font-medium">As the cloud sees the studio</p>
              {view.cloudView.map((n) => (
                <p key={`${n.siteId}/${n.nodeId}`} className="text-xs text-muted-foreground">
                  {n.siteId} · {n.nodeId} ({n.role}) · link {n.linkState} · last seen {n.lastSeen}
                </p>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
